/**
 * QR Code Scanner Component
 *
 * Continuous QR code scanning using html5-qrcode library.
 * Designed for voice agent integration with front camera support.
 *
 * Features:
 * - Front camera by default (facingMode: 'user')
 * - Continuous scanning mode (camera stays active)
 * - Configurable cooldown to prevent rapid re-scanning
 * - Visual feedback on successful scan
 * - Error handling with user-friendly messages
 *
 * IMPORTANT: Do NOT override html5-qrcode's video CSS (object-fit, width, height).
 * The library computes scan regions based on the video's native resolution.
 * CSS overrides desync the visual display from the actual scan coordinates.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeResult, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Camera, X, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface QRCodeScannerProps {
  /** Callback when a QR code is successfully scanned */
  onScan: (code: string) => void;
  /** Callback when scanner encounters an error */
  onError?: (error: string) => void;
  /** Callback when user closes the scanner */
  onClose?: () => void;
  /** Use front camera (default: true for voice agent use case) */
  useFrontCamera?: boolean;
  /** Cooldown in ms between scans of the same code (default: 2000) */
  cooldownMs?: number;
  /** Show close button (default: true) */
  showCloseButton?: boolean;
  /** Custom className for container */
  className?: string;
}

interface ScanFeedback {
  code: string;
  timestamp: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

// Constructor config — controls decoder behavior (what formats to scan)
const CONSTRUCTOR_CONFIG = {
  formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
  experimentalFeatures: { useBarCodeDetectorIfSupported: true },
  verbose: false,
};

// Scan config — controls camera behavior (how to scan)
// Note: videoConstraints is built dynamically in startScanner based on useFrontCamera prop.
const BASE_SCAN_CONFIG = {
  fps: 15,
  // No qrbox — scan the full video frame for maximum detection range.
  // With a 250x250 box, QR codes had to be very close to fill the scan region.
  // Full-frame scanning detects codes at any position and distance.
  // No aspectRatio — let the camera use its native ratio (4:3 or 16:9)
  // to maximize resolution. Forcing 1.0 crops the feed or picks lower res.
};

const DEFAULT_COOLDOWN_MS = 2000;

// =============================================================================
// COMPONENT
// =============================================================================

export const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onScan,
  onError,
  onClose,
  useFrontCamera = true,
  cooldownMs = DEFAULT_COOLDOWN_MS,
  showCloseButton = true,
  className = '',
}) => {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScanRef = useRef<ScanFeedback | null>(null);

  const [isInitialized, setIsInitialized] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastScannedCode, setLastScannedCode] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Generate unique ID for scanner element
  const scannerId = useRef(`qr-reader-${Date.now()}`);

  // Stable refs for callbacks — prevents camera restart when parent re-renders.
  // Without this, every round/state change in consonant practice recreates
  // onScan → handleScanSuccess → startScanner → useEffect fires → camera restarts.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // ---------------------------------------------------------------------------
  // Scan Handler with Cooldown
  // ---------------------------------------------------------------------------

  const handleScanSuccess = useCallback((decodedText: string, _result: Html5QrcodeResult) => {
    const now = Date.now();
    const lastScan = lastScanRef.current;

    // Check cooldown - don't re-scan same code within cooldown period
    if (lastScan && lastScan.code === decodedText && now - lastScan.timestamp < cooldownMs) {
      return;
    }

    // Update last scan
    lastScanRef.current = { code: decodedText, timestamp: now };
    setLastScannedCode(decodedText);

    // Show success feedback
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 1000);

    // Notify parent via stable ref (no dependency on onScan prop)
    console.log('[QRCodeScanner] Scanned:', decodedText);
    onScanRef.current(decodedText);
  }, [cooldownMs]);

  const handleScanFailure = useCallback((_errorMessage: string) => {
    // Scan failures are expected during continuous scanning (no QR in frame)
    // Only log actual errors, not "no QR code found" messages
  }, []);

  // ---------------------------------------------------------------------------
  // Scanner Initialization
  // ---------------------------------------------------------------------------

  const startScanner = useCallback(async () => {
    if (!containerRef.current || scannerRef.current) return;

    try {
      setError(null);
      setIsInitialized(false);

      const scanner = new Html5Qrcode(scannerId.current, CONSTRUCTOR_CONFIG);
      scannerRef.current = scanner;

      const facingMode = useFrontCamera ? 'user' : 'environment';

      await scanner.start(
        { facingMode },
        {
          ...BASE_SCAN_CONFIG,
          // Front camera is already mirrored by the browser — scanning the
          // flipped copy is redundant and doubles decode work per frame.
          disableFlip: useFrontCamera,
          // Request HD resolution (3.4x more pixels than default 640x480).
          // When videoConstraints is present, it overrides the first argument.
          videoConstraints: {
            facingMode,
            width: { min: 640, ideal: 1280 },
            height: { min: 480, ideal: 720 },
          },
        },
        handleScanSuccess,
        handleScanFailure
      );

      setIsInitialized(true);
      setIsScanning(true);
      console.log('[QRCodeScanner] Started with', facingMode, 'camera');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start scanner';
      console.error('[QRCodeScanner] Start error:', err);

      // Provide user-friendly error messages
      let userMessage = errorMessage;
      if (errorMessage.includes('NotAllowedError') || errorMessage.includes('Permission')) {
        userMessage = 'Camera permission denied. Please allow camera access to scan QR codes.';
      } else if (errorMessage.includes('NotFoundError') || errorMessage.includes('no camera')) {
        userMessage = 'No camera found. Please ensure your device has a camera.';
      } else if (errorMessage.includes('NotReadableError')) {
        userMessage = 'Camera is in use by another application.';
      }

      setError(userMessage);
      onErrorRef.current?.(userMessage);
    }
  }, [useFrontCamera, handleScanSuccess, handleScanFailure]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        await scannerRef.current.clear();
      } catch (err) {
        console.warn('[QRCodeScanner] Stop error:', err);
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
    setIsInitialized(false);
  }, []);

  const restartScanner = useCallback(async () => {
    await stopScanner();
    // Small delay to ensure cleanup
    setTimeout(startScanner, 100);
  }, [stopScanner, startScanner]);

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  useEffect(() => {
    startScanner();

    return () => {
      stopScanner();
    };
  }, [startScanner, stopScanner]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className={`relative h-full flex items-center justify-center bg-black ${className}`}>
      {/* Scanner Container — centered, html5-qrcode manages its own video layout */}
      <div
        ref={containerRef}
        className="relative rounded-2xl overflow-hidden bg-black w-full"
        style={{ minHeight: 300 }}
      >
        {/* Scanner Element — DO NOT add CSS overrides on video/inner divs */}
        <div
          id={scannerId.current}
          className="w-full"
          style={{ minHeight: 300 }}
        />

        {/* Success Overlay */}
        {showSuccess && (
          <div className="absolute inset-0 flex items-center justify-center bg-green-500/20 backdrop-blur-sm z-10 animate-pulse">
            <div className="flex flex-col items-center gap-2">
              <CheckCircle2 className="w-16 h-16 text-green-500" />
              <span className="text-white font-bold text-lg">Scanned!</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {!isInitialized && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
            <div className="flex flex-col items-center gap-3 text-white">
              <Camera className="w-12 h-12 animate-pulse" />
              <span className="text-sm">Starting camera...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-10 p-6">
            <div className="flex flex-col items-center gap-4 text-center max-w-sm">
              <AlertCircle className="w-12 h-12 text-red-400" />
              <p className="text-white text-sm">{error}</p>
              <button
                onClick={restartScanner}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Close Button */}
        {showCloseButton && onClose && (
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors z-20"
            aria-label="Close scanner"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Scanning Indicator */}
        {isScanning && isInitialized && !showSuccess && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 backdrop-blur-sm rounded-full z-10">
            <div className="flex items-center gap-2 text-white text-sm">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Point at QR code
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QRCodeScanner;
